import { useEffect, useState } from "react";
import { api } from "../api";
import { NumberField } from "./NumberField";
import type { NotificationKind } from "./Portal";

const describe = (minutes: number) => {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60); const rest = minutes % 60;
  return `${hours} hour${hours === 1 ? "" : "s"}${rest ? ` ${rest} min` : ""}`;
};

export function SessionSettingsPanel({ onMessage }: { onMessage: (message: string, kind?: NotificationKind) => void }) {
  const [ttl, setTtl] = useState(720);
  const [idle, setIdle] = useState(60);
  const [saved, setSaved] = useState({ ttl: 720, idle: 60 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const report = (error: Error) => onMessage(error.message, "error");

  useEffect(() => {
    api.sessionSettings().then((settings) => {
      const next = { ttl: Math.round(settings.ttlSeconds / 60), idle: Math.round(settings.idleSeconds / 60) };
      setTtl(next.ttl); setIdle(next.idle); setSaved(next); setLoading(false);
    }).catch((error: Error) => { report(error); setLoading(false); });
  }, []);

  const changed = ttl !== saved.ttl || idle !== saved.idle;
  const invalid = idle > ttl;

  async function save() {
    setSaving(true);
    try {
      const settings = await api.updateSessionSettings({ ttlSeconds: ttl * 60, idleSeconds: idle * 60 });
      const next = { ttl: Math.round(settings.ttlSeconds / 60), idle: Math.round(settings.idleSeconds / 60) };
      setTtl(next.ttl); setIdle(next.idle); setSaved(next);
      onMessage("Session settings saved. They apply to new sign-ins and the next request from existing sessions.");
    } finally { setSaving(false); }
  }

  if (loading) return <section className="session-settings"><h3>Sessions</h3><div className="skeleton-list"><span className="skeleton-row"/></div></section>;

  return <section className="session-settings">
    <div className="section-heading"><div><h3>Sessions</h3><p>How long people stay signed in. Changes take effect immediately — no deployment needed.</p></div></div>
    <div className="session-fields">
      <div className="session-field">
        <label htmlFor="session-ttl"><strong>Maximum session length</strong><small>Signed out after this long, even while actively working. Between 15 minutes and 24 hours.</small></label>
        <div className="session-control"><NumberField label="Maximum session length in minutes" value={ttl} min={15} max={1440} step={15} onChange={setTtl}/><span>minutes · {describe(ttl)}</span></div>
      </div>
      <div className="session-field">
        <label htmlFor="session-idle"><strong>Inactivity timeout</strong><small>Signed out after this long without activity. Background data refreshes do not count. Between 5 minutes and 24 hours.</small></label>
        <div className="session-control"><NumberField label="Inactivity timeout in minutes" value={idle} min={5} max={1440} step={5} onChange={setIdle}/><span>minutes · {describe(idle)}</span></div>
      </div>
    </div>
    {invalid && <p className="cutoff-notice">The inactivity timeout cannot be longer than the maximum session length — the session would always end first.</p>}
    <div className="pricing-savebar">
      <span>{changed ? "Unsaved changes" : `Currently ${describe(saved.ttl)} maximum, ${describe(saved.idle)} idle`}</span>
      {changed && <button className="secondary-button" onClick={() => { setTtl(saved.ttl); setIdle(saved.idle); }}>Discard</button>}
      <button className="save-button" disabled={!changed || invalid || saving} onClick={() => save().catch(report)}>{saving ? "Saving…" : "Save settings"}</button>
    </div>
  </section>;
}
